import {
	API_CRYPTO_TYPE,
	API_ETC_TYPE,
	API_ETF_TYPE,
	API_INDEX_TYPE,
	API_LEVERAGED_TOKEN_TYPE,
	API_METAL_TYPE,
	API_SECURITY_TYPE,
	API_STOCK_TYPE,
	EXCLUDED_ASSET_SYMBOLS,
	SECURITY_ASSET_GROUP_LIST,
	TRADABLES_TYPE
} from "@helpers/constants";
import { Tradable } from "@models/interfaces/tradable";
import { Timespan } from "@helpers/const/timespan";
import { BpOption } from "@models/ui-components/bp-option";
import { AssetGroupName } from "@modules/+new/shared/const/enums/asset-group-name.enum";
import { BuyAssetListModel } from "@modules/trade/components/buy-asset-list/buy-asset-list.facade";
import { AssetFragment } from "../../../api/graphql/bff/generated/fragments.service.graphql-gen";
import { MarginAssetTradableAdapter } from "@models/interfaces/marginTradableAdapter";
import { Conversion, Price } from "@models";
import { EstimatePricesQuery } from "../../../api/graphql/offer/resources/generated/estimate.prices.service.graphql-gen";
import { EstimateIndexPricesQuery } from "../../../api/graphql/offer/generated/estiamte-index.prices.service.graphql-gen";

export function getTradableChange(
	asset: Tradable,
	timespan: Timespan
): { changePercent: number; changeAmount: number } {
	switch (timespan) {
		case Timespan.Day:
			return {
				changePercent: asset.getChange24h(),
				changeAmount: asset.getChange24hAmount()
			};
		case Timespan.Week:
			return {
				changePercent: asset.getChange1w(),
				changeAmount: asset.getChange1wAmount()
			};
		case Timespan.Month:
			return {
				changePercent: asset.getChange1m(),
				changeAmount: asset.getChange1mAmount()
			};
		case Timespan.SixMonths:
			return {
				changePercent: asset.getChange6m(),
				changeAmount: asset.getChange6mAmount()
			};
		case Timespan.Year:
			return {
				changePercent: asset.getChange1y(),
				changeAmount: asset.getChange1yAmount()
			};
		case Timespan.FiveYears:
			return {
				changePercent: asset.getChange5y(),
				changeAmount: asset.getChange5yAmount()
			};
		default:
			return null;
	}
}

/**
 * check if type is derivative (stock, etc or etf)
 * @param type TRADABLES_TYPE
 * @returns boolean
 */
export function isTypeDerivative(type: TRADABLES_TYPE): boolean {
	return type === TRADABLES_TYPE.STOCK || type === TRADABLES_TYPE.ETC || type === TRADABLES_TYPE.ETF;
}

export function isLeveragedToken(type: TRADABLES_TYPE): boolean {
	return type === TRADABLES_TYPE.LEVERAGED_TOKEN;
}

export function getBuyAssetListFilters(filters: BpOption[], tradables: Tradable[]): BpOption[] {
	const isSomeLeverageAvailable = tradables
		.filter((t) => t.getAssetGroupName() === AssetGroupName.LeveragedToken)
		.some((t) => t.getAssetSettings().isPriceOrderAvailable());
	const isSomeIndexAvailable = tradables
		.filter((t) => t.getAssetGroupName() === AssetGroupName.Index)
		.some((t) => t.getAssetSettings().isPriceOrderAvailable());
	const isSomeStockAvailable = tradables
		.filter((t) => t.getAssetGroupName() === AssetGroupName.Stock)
		.some((t) => t.getAssetSettings().isPriceOrderAvailable());
	const isSomeEtcAvailable = tradables
		.filter((t) => t.getAssetGroupName() === AssetGroupName.Etc)
		.some((t) => t.getAssetSettings().isPriceOrderAvailable());
	const isSomeEtfAvailable = tradables
		.filter((t) => t.getAssetGroupName() === AssetGroupName.Etf)
		.some((t) => t.getAssetSettings().isPriceOrderAvailable());
	const isSomeMetalAvailable = tradables
		.filter((t) => t.getAssetGroupName() === AssetGroupName.Metal)
		.some((t) => t.getAssetSettings().isPriceOrderAvailable());
	const isSomeSecurityTokenAvailable = tradables
		.filter((t) => t.getAssetGroupName() === AssetGroupName.SecurityToken)
		.some((t) => t.getAssetSettings().isPriceOrderAvailable());

	return filters.filter((f) => {
		return (
			(isSomeLeverageAvailable || f.value !== API_LEVERAGED_TOKEN_TYPE) &&
			(isSomeIndexAvailable || f.value !== API_INDEX_TYPE) &&
			(isSomeStockAvailable || f.value !== API_STOCK_TYPE) &&
			(isSomeEtcAvailable || f.value !== API_ETC_TYPE) &&
			(isSomeEtfAvailable || f.value !== API_ETF_TYPE) &&
			(isSomeMetalAvailable || f.value !== API_METAL_TYPE) &&
			(isSomeSecurityTokenAvailable || f.value !== API_SECURITY_TYPE)
		);
	});
}

export function isDigiAsset(tradableType: TRADABLES_TYPE | string): boolean {
	return (
		tradableType === TRADABLES_TYPE.STOCK ||
		tradableType === TRADABLES_TYPE.ETF ||
		tradableType === TRADABLES_TYPE.ETC
	);
}

export function isStockType(type: string): boolean {
	return SECURITY_ASSET_GROUP_LIST.includes(type);
}

export function filterTradablesPriceOrder(isPriceOrder: boolean, tradables: Tradable[]): Tradable[] {
	return tradables.filter((tradable) =>
		isPriceOrder ? tradable.getAssetSettings()?.isPriceOrderAvailable() : tradable
	);
}

export function filterTradablesByCondition(tradables: Tradable[]): Tradable[] {
	return tradables.filter(
		(tradable) =>
			!isDigiAsset(tradable.getCurrencyType()) &&
			!tradable.isIndexOnly() &&
			tradable.isBuyActive() &&
			tradable.getAssetGroupName() !== AssetGroupName.FiatEarn
	);
}

export function filterTradablesByPill(pill: BpOption, tradables: Tradable[]): Tradable[] {
	let assetGroupName: AssetGroupName;

	if (API_CRYPTO_TYPE === pill.value) {
		return tradables.filter(
			(tradable) =>
				tradable.getAssetGroupName() === AssetGroupName.Crypto ||
				tradable.getAssetGroupName() === AssetGroupName.Token
		);
	}

	switch (pill.value) {
		case API_STOCK_TYPE:
			assetGroupName = AssetGroupName.Stock;
			break;
		case API_ETF_TYPE:
			assetGroupName = AssetGroupName.Etf;
			break;
		case API_LEVERAGED_TOKEN_TYPE:
			assetGroupName = AssetGroupName.LeveragedToken;
			break;
		case API_INDEX_TYPE:
			assetGroupName = AssetGroupName.Index;
			break;
		case API_ETC_TYPE:
			assetGroupName = AssetGroupName.Etc;
			break;
		case API_METAL_TYPE:
			assetGroupName = AssetGroupName.Metal;
			break;
		case API_SECURITY_TYPE:
			assetGroupName = AssetGroupName.SecurityToken;
			break;
		default:
			return tradables;
	}

	return tradables.filter((tradable) => tradable.getAssetGroupName() === assetGroupName);
}

export function filterTradableUKUsers(tradables: Tradable[]): Tradable[] {
	return tradables.filter((f) => {
		return (
			!EXCLUDED_ASSET_SYMBOLS.includes(f.getSymbol()) &&
			f.getAssetGroupName() !== API_STOCK_TYPE &&
			f.getAssetGroupName() !== API_METAL_TYPE &&
			f.getAssetGroupName() !== API_ETC_TYPE &&
			f.getAssetGroupName() !== API_ETF_TYPE
		);
	});
}

export function filterSortTradables(
	buyListModel: BuyAssetListModel,
	tradables: Tradable[],
	selectedPill: BpOption
): Tradable[] {
	const { sortingChange, sortingPrice, sortingName } = buyListModel;

	if (sortingChange.active === "change" && sortingChange.direction !== "") {
		return tradables.sort((t1, t2) => {
			const change1 = t1.getChange24h();
			const change2 = t2.getChange24h();
			return sortingChange.direction === "asc" ? change1 - change2 : change2 - change1;
		});
	}

	if (sortingPrice.active === "price" && sortingPrice.direction !== "") {
		return tradables.sort((t1, t2) => {
			const price1 = t1.getPrice();
			const price2 = t2.getPrice();
			return sortingPrice.direction === "asc" ? price1 - price2 : price2 - price1;
		});
	}

	if (sortingName.active === "name" && sortingName.direction !== "") {
		return tradables.sort((t1, t2) => {
			const name1 = t1.getName();
			const name2 = t2.getName();
			return sortingName.direction === "asc" ? name1.localeCompare(name2) : name2.localeCompare(name1);
		});
	}

	// Some AssetGroups doesn't have MC, but BE return getSort field on Tradables Array
	if (
		selectedPill.value === API_LEVERAGED_TOKEN_TYPE ||
		selectedPill.value === API_ETC_TYPE ||
		selectedPill.value === API_INDEX_TYPE
	) {
		return tradables.sort((t1, t2) => (t1.getAssetSettings().getSort() < t2.getAssetSettings().getSort() ? 1 : -1));
	}
	if (selectedPill.value === API_ETF_TYPE) {
		return tradables.sort((t1, t2) => (t1.getAssetSettings().getSort() > t2.getAssetSettings().getSort() ? 1 : -1));
	}

	// If there is no sorting, sort by market cap
	return tradables.sort((t1, t2) =>
		t1.getAssetPrices()?.getMarketCap() > t2.getAssetPrices()?.getMarketCap() ? -1 : 1
	);
}

export function filterTradablesBySearch(searchValue: string, tradables: Tradable[]): Tradable[] {
	return tradables.filter(
		(tradable) =>
			tradable.getName().toLowerCase().includes(searchValue.toLowerCase()) ||
			tradable.getSymbol().toLowerCase().includes(searchValue.toLowerCase())
	);
}

export function filterLeveragedType(
	marginAssets: AssetFragment[],
	tradables: Tradable[],
	searchValue: string
): Tradable[] {
	const marginTradables = marginAssets.map((asset) => new MarginAssetTradableAdapter(asset, tradables));

	if (!searchValue) {
		return marginTradables;
	}

	return marginTradables.filter(
		(t) =>
			t.getName().toLowerCase().includes(searchValue.toLowerCase()) ||
			t.getSymbol().toLowerCase().includes(searchValue.toLowerCase())
	);
}

export function roundUpToHundredth(value: number): number {
	return Math.ceil(value * 100) / 100;
}

export function detectPrecisionHelper(value: number): number {
	const valueString = value.toString();
	const decimalIndex = valueString.search(/[.,]/);
	const numAfterDecimal = valueString.substring(decimalIndex + 1).search(/[1-9]/);

	return numAfterDecimal + 2;
}

/**
 * Convert object to array if value is true
 * @param obj object
 * @returns string[]
 */
export function convertObjectToArrayIfValTrue(obj: { [key: string]: boolean }): string[] {
	//if onbject is empty or all values are false return empty array
	if (!obj || Object.keys(obj).length === 0) {
		return [];
	}

	return Object.keys(obj).filter((key) => obj[key]);
}

/**
 * Extract the base path from a URL
 * @param url URL to extract the base path from
 * @returns Base path
 */
export function extractBasePath(url: string): string {
	// Regular expression to match the base path before the modal route
	const regex = /^([^()]+)(\(.*\))?$/;
	const match = url.match(regex);

	if (match) {
		return match[1]; // Return the base path
	}

	return url; // Return the original URL if no match is found
}

/**
 * Avoid e.g a .5 (a decimal number without 0.*) will be converrted to 0.5
 * @param url URL to extract the base path from
 * @returns Base path
 */
export function normalizeDecimalNumberThatStartWithoutZero(value: string): boolean {
	// to avoid e.g a .5 or ,5 (a decimal number without 0.*) will be converrted to 0.5
	const regex = /^[.,]\d/;
	return regex.test(value);
}

export function mapEstimatePricesToPrice(query: EstimatePricesQuery["estimatePrices"]): Price {
	const price = new Price();

	// wallet
	price["wallet"] = new Conversion();
	price["wallet"]["crypto"] = query.max.asset;
	price["wallet"]["fiat"] = query.max.fiat;

	// default_amounts
	price["default_amounts"] = [new Conversion()];
	price["default_amounts"][0]["fiat"] = query.max.fiat;
	price["default_amounts"][0]["crypto"] = query.max.asset;
	price["default_amounts"][0]["fiat_bfc"] = query.max.fiat;
	price["default_amounts"][0]["crypto_bfc"] = query.max.asset;

	// price
	price["price"] = new Conversion();
	price["price"]["fiat"] = query.price;

	// minimum
	price["minimum"] = new Conversion();
	price["minimum"]["crypto"] = query.min.asset;
	price["minimum"]["fiat"] = query.min.fiat;

	//max
	price["maximum_external"]["fiat"] = query.max.fiat;
	price["maximum_external"]["crypto"] = query.max.asset;
	price["maximum_internal"]["crypto"] = query.max.asset;
	price["maximum_internal"]["fiat"] = query.max.fiat;

	// instant_trade_bonus_rate
	price["instant_trade_bonus_rate"] = "";

	// app_test_trading_warning
	price["app_test_trading_warning"] = query.appropriatenessTest.required;

	// maxAmountWithoutFee
	price["maxAmountWithoutFee"] = null;

	return price;
}

export function mapEstimateIndexPricesToPrice(query: EstimateIndexPricesQuery["estimateIndexPrices"]): Price {
	const price = new Price();

	// wallet
	price["wallet"] = new Conversion();
	price["wallet"]["fiat"] = query.fiat.max;
	price["wallet"]["crypto"] = query.fiat.max;

	price["default_amounts"] = [new Conversion()];
	price["default_amounts"][0]["fiat"] = query.fiat.max;
	price["default_amounts"][0]["crypto"] = query.fiat.max;
	price["default_amounts"][0]["fiat_bfc"] = query.fiat.max;
	price["default_amounts"][0]["crypto_bfc"] = query.fiat.max;

	//max
	price["maximum_external"]["fiat"] = query.fiat.max;
	price["maximum_external"]["crypto"] = query.fiat.max;
	price["maximum_internal"]["crypto"] = query.fiat.max;
	price["maximum_internal"]["fiat"] = query.fiat.max;

	price["maximum_external"]["fiat"] = query.fiat.max;
	price["maximum_external"]["crypto"] = query.fiat.max;

	price["minimum"]["fiat"] = query.fiat.min;
	price["minimum"]["crypto"] = query.fiat.min;

	return price;
}
